import { classifyPixel, worldToPixel } from "./geometry.js";
/**
 * TCR nav2 footprint, 1.10 × 0.47 m with base_link 0.41 m from the rear: the costmaps' in
 * tcr robot_config/tcr/config/navigation/nav2_params.yaml, and the box global_path_harness
 * plans with. (nav2_params_fastlio.yaml and route_network.json's robot block say 0.42 m wide.)
 */
export const TCR_FOOTPRINT = [
    { x: 0.69, y: -0.235 },
    { x: 0.69, y: 0.235 },
    { x: -0.41, y: 0.235 },
    { x: -0.41, y: -0.235 },
];
/** Clearance below this is tight (route_network.json safety.minimum_clearance). */
export const MIN_CLEARANCE_M = 0.05;
/** Spacing of the points the clearance is measured at. */
const SAMPLE_STEP_M = 0.02;
/** Value returned when part of the footprint is off the map image. */
export const OFF_MAP_CLEARANCE = -1;
export function clearanceLevel(c) {
    return c < 0 ? "collision" : c < MIN_CLEARANCE_M ? "tight" : "clear";
}
/** The footprint's corners in the map frame at `pose`. */
export function footprintAt(pose, fp = TCR_FOOTPRINT) {
    const c = Math.cos(pose.theta);
    const s = Math.sin(pose.theta);
    return fp.map((p) => ({ x: pose.x + p.x * c - p.y * s, y: pose.y + p.x * s + p.y * c }));
}
function inside(p, poly) {
    // Points on an edge count as inside: the edge is the body.
    let hit = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i];
        const b = poly[j];
        const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
        if (Math.abs(cross) < 1e-9 && p.x >= Math.min(a.x, b.x) - 1e-9 && p.x <= Math.max(a.x, b.x) + 1e-9 && p.y >= Math.min(a.y, b.y) - 1e-9 && p.y <= Math.max(a.y, b.y) + 1e-9)
            return true;
        if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x)
            hit = !hit;
    }
    return hit;
}
const sampleCache = new WeakMap();
/** Points of the footprint (edges included) on an even grid of about 2 cm, in base_link. */
export function footprintSamples(fp = TCR_FOOTPRINT) {
    const hit = sampleCache.get(fp);
    if (hit)
        return hit;
    const xs = fp.map((p) => p.x);
    const ys = fp.map((p) => p.y);
    const x0 = Math.min(...xs);
    const y0 = Math.min(...ys);
    const nx = Math.ceil((Math.max(...xs) - x0) / SAMPLE_STEP_M);
    const ny = Math.ceil((Math.max(...ys) - y0) / SAMPLE_STEP_M);
    const dx = nx ? (Math.max(...xs) - x0) / nx : 0;
    const dy = ny ? (Math.max(...ys) - y0) / ny : 0;
    const out = [];
    for (let i = 0; i <= nx; i++)
        for (let j = 0; j <= ny; j++) {
            const p = { x: x0 + i * dx, y: y0 + j * dy };
            if (inside(p, fp))
                out.push(p);
        }
    sampleCache.set(fp, out);
    return out;
}
/** Field value where no cell is occupied: large, but finite so bilinear weights of 0 stay 0. */
export const NO_OBSTACLE_M = 1e9;
/** 1-D squared distance transform of `f` (Felzenszwalb & Huttenlocher), into `d`. */
function edt1d(f, n, d, v, z) {
    let k = 0;
    v[0] = 0;
    z[0] = -Infinity;
    z[1] = Infinity;
    for (let q = 1; q < n; q++) {
        let p = v[k];
        let s = (f[q] + q * q - (f[p] + p * p)) / (2 * q - 2 * p);
        while (s <= z[k]) {
            k--;
            p = v[k];
            s = (f[q] + q * q - (f[p] + p * p)) / (2 * q - 2 * p);
        }
        k++;
        v[k] = q;
        z[k] = s;
        z[k + 1] = Infinity;
    }
    k = 0;
    for (let q = 0; q < n; q++) {
        while (z[k + 1] < q)
            k++;
        const p = v[k];
        d[q] = (q - p) * (q - p) + f[p];
    }
}
/**
 * Build the clearance field for a map image. `pixels` holds one grey value per pixel at
 * `pixels[(row * width + col) * channels]` (row 0 = top), so RGBA ImageData can be passed as is.
 */
export function clearanceField(pixels, meta, t, channels = 1) {
    const { width: W, height: H } = meta;
    // "Far" must exceed any real squared distance yet stay small enough that adding q² to it
    // is exact in a double (1e20 would swallow q² and corrupt the parabola intersections).
    const far = 2 * (W * W + H * H) + 1;
    const sq = new Float64Array(W * H);
    for (let i = 0; i < W * H; i++)
        sq[i] = classifyPixel(pixels[i * channels], t.negate, t.occupiedThresh, t.freeThresh) === "occupied" ? 0 : far;
    const n = Math.max(W, H);
    const f = new Float64Array(n);
    const d = new Float64Array(n);
    const v = new Int32Array(n);
    const z = new Float64Array(n + 1);
    for (let c = 0; c < W; c++) {
        for (let r = 0; r < H; r++)
            f[r] = sq[r * W + c];
        edt1d(f, H, d, v, z);
        for (let r = 0; r < H; r++)
            sq[r * W + c] = d[r];
    }
    const dist = new Float32Array(W * H);
    for (let r = 0; r < H; r++) {
        for (let c = 0; c < W; c++)
            f[c] = sq[r * W + c];
        edt1d(f, W, d, v, z);
        for (let c = 0; c < W; c++)
            dist[r * W + c] = d[c] >= far ? NO_OBSTACLE_M : Math.sqrt(d[c]) * meta.resolution;
    }
    return { meta, dist };
}
/** footprintSamples as a flat [x0, y0, x1, y1, …] array: the hot loop below reads it per pose. */
const flatCache = new WeakMap();
function flatSamples(fp) {
    let hit = flatCache.get(fp);
    if (!hit) {
        hit = Float64Array.from(footprintSamples(fp).flatMap((p) => [p.x, p.y]));
        flatCache.set(fp, hit);
    }
    return hit;
}
/**
 * Clearance of the footprint at `pose`, metres (see the module comment). OFF_MAP_CLEARANCE when
 * any part of it is off the map image; about NO_OBSTACLE_M on a map with no occupied cell.
 *
 * Swept-path checks call this thousands of times per path, so body points go straight to pixel
 * coordinates through one affine map per pose (what worldToPixel does point by point).
 */
export function footprintClearance(field, pose, fp = TCR_FOOTPRINT) {
    const { meta, dist } = field;
    const W = meta.width;
    const H = meta.height;
    const r = meta.resolution;
    // Body point (bx, by) → fractional pixel index measured from the first cell centre (hence −0.5).
    const { u, v } = worldToPixel(pose.x, pose.y, meta);
    const u0 = u - 0.5;
    const v0 = v - 0.5;
    const rel = pose.theta - meta.origin[2];
    const c = Math.cos(rel) / r;
    const s = Math.sin(rel) / r;
    const pts = flatSamples(fp);
    let min = Infinity;
    for (let i = 0; i < pts.length; i += 2) {
        const bx = pts[i];
        const by = pts[i + 1];
        const fx = u0 + bx * c - by * s;
        const fy = v0 - bx * s - by * c;
        if (fx < 0 || fy < 0 || fx > W - 1 || fy > H - 1)
            return OFF_MAP_CLEARANCE;
        const x0 = Math.floor(fx);
        const y0 = Math.floor(fy);
        const x1 = x0 + 1 < W ? x0 + 1 : x0;
        const y1 = y0 + 1 < H ? y0 + 1 : y0;
        const ax = fx - x0;
        const ay = fy - y0;
        const top = dist[y0 * W + x0] * (1 - ax) + dist[y0 * W + x1] * ax;
        const bot = dist[y1 * W + x0] * (1 - ax) + dist[y1 * W + x1] * ax;
        const val = top * (1 - ay) + bot * ay;
        if (val < min)
            min = val;
    }
    return min - r / 2;
}
//# sourceMappingURL=footprint.js.map